import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  forwardRef
} from '@angular/core';

import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator
} from '@angular/forms';

import { SharedModule } from 'src/app/shared/shared.module';
import { MatInputModule } from '@angular/material/input';
import { OffsetDateTime } from 'src/app/generated/common/date_time/date_time';
import { DateTime } from 'luxon';

/**
 * This class allows a user to select a date with a selected Timezone.
 * If a time zone is not specified it will use the current timezone.
 */
@Component({
  selector: 'app-material-offset-date-time',
  templateUrl: './material-offset-date-time.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [SharedModule, MatInputModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MaterialOffsetDateTimeComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => MaterialOffsetDateTimeComponent),
      multi: true,
    },
  ],
})
export class MaterialOffsetDateTimeComponent
implements ControlValueAccessor, Validator {

  onChange = (_value: OffsetDateTime | null) => {};
  onTouched = () => {};

  @Output() focused = new EventEmitter<any>();

  _isDisabled = false;
  _maxDate?: string;
  _minDate?: string;

  _selectedDate?: string;
  _label?: string;
  _type?: string;
  _zoneId?: string;
  _dateFormat = 'yyyy-LL-dd HH:mm';
  _value?: OffsetDateTime;
  _required = false;

  @Input() set maxDate(value: string) {
    const max = DateTime
      .fromISO(value, { zone: 'utc' })
      .toLocal()
      .toFormat("yyyy-LL-dd'T'HH:mm");

    this._maxDate = max;
  }

  @Input() set minDate(value: string) {
    const min = DateTime
      .fromISO(value, { zone: 'utc' })
      .toLocal()
      .toFormat("yyyy-LL-dd'T'HH:mm");

    this._minDate = min;
  }

  @Input() set label(value: string) {
    if (value) {
      this._label = value;
    }
  }

  @Input() set type(value: string) {
    if (value) {
      this._type = value;

      if (this._type === 'date') {
        this._dateFormat = 'yyyy-LL-dd';
      }
    }
  }

  @Input() set zoneId(value: string) {
    if (value) {
      this._zoneId = value;
      this._selectedDate = undefined;
    }
  }

  @Input() set disabled(value: boolean) {
    this._isDisabled = value;
  }

  @Input() required = false;

  @Input() set date(value: OffsetDateTime) {
    if (value) {
      const dt = DateTime
        .fromISO(value.isoDate || '', { zone: 'utc' })
        .setZone(value.offset)
        .toFormat(this._dateFormat);

      this._value = value;
      this._selectedDate = dt;
    }
  }

  @Output() dateChange = new EventEmitter<OffsetDateTime>();

  selectedDateChanged(event: Event): void {
    const value = (event.target as HTMLInputElement).value;

    if (value) {
      const zonedDateTime = DateTime.fromISO(value, {
        zone: this._zoneId || undefined
      });

      this._value = {
        isoDate:
          this._type === 'date'
            ? zonedDateTime.endOf('day').toUTC().toISO() || ''
            : zonedDateTime.toUTC().toISO() || '',

        offset: zonedDateTime.toFormat('ZZ')
      };
    } else {
      this._value = undefined;
    }

    this.dateChange.emit(this._value);
    this.onChange(this._value!);
    this.onTouched();
  }

  writeValue(value: OffsetDateTime | null): void {
    if (value && value.isoDate) {
      const dt = DateTime
        .fromISO(value.isoDate, { zone: 'utc' })
        .setZone(value.offset)
        .toFormat(this._dateFormat);

      this._value = value;
      this._selectedDate = dt;
    } else {
      this._value = undefined;
      this._selectedDate = undefined;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  validate(_control: AbstractControl): ValidationErrors | null {
    if (this._required && !this._value) {
      return { required: true };
    }

    return null;
  }
}